// Messaging building blocks (contracts/messaging.md "Shared building blocks"): localized content, images,
// actions and the one-click translation. Announcements (broadcasts.ts), banners (banners.ts) and support
// (support.ts) build on these. Kept apart from client.ts / queries.ts like Ads and the Studio.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { request } from "./client";

// ---------------------------------------------------------------- languages and content

/** The 13 app languages (contracts/i18n.md §1), English first. */
export const LOCALES = ["en", "es", "pt-BR", "fr", "de", "it", "ja", "ko", "zh-Hans", "zh-Hant", "ru", "tr", "ar"] as const;
export type Locale = (typeof LOCALES)[number];
/** Every language a translation fills (all but English). */
export const TRANSLATE_TARGETS = LOCALES.filter((l) => l !== "en");

export const LOCALE_LABEL: Record<Locale, string> = {
  en: "English",
  es: "Spanish",
  "pt-BR": "Portuguese (Brazil)",
  fr: "French",
  de: "German",
  it: "Italian",
  ja: "Japanese",
  ko: "Korean",
  "zh-Hans": "Chinese (Simplified)",
  "zh-Hant": "Chinese (Traditional)",
  ru: "Russian",
  tr: "Turkish",
  ar: "Arabic",
};
export const RTL_LOCALES: ReadonlySet<Locale> = new Set(["ar"]);

/** Field limits, as backend/api checks them (domain/content.ts). */
export const CONTENT_LIMITS = { title: 80, body: 300, detail: 4000, cta: 30 } as const;
export type ContentField = keyof typeof CONTENT_LIMITS;

export const ContentEntry = z.object({
  title: z.string(),
  body: z.string(),
  detail: z.string().optional(),
  cta: z.string().optional(),
});
export type ContentEntry = z.infer<typeof ContentEntry>;
/** Per locale `{ title, body, detail?, cta? }`; `en` is always there. */
export const Content = z.partialRecord(z.enum(LOCALES), ContentEntry);
export type Content = z.infer<typeof Content>;

// ---------------------------------------------------------------- images

export const MEDIA_MAX_BYTES = 1_400_000;
export const MEDIA_MIMES = ["image/png", "image/jpeg", "image/webp"] as const;
export type MediaMime = (typeof MEDIA_MIMES)[number];

const count = z.number().int().min(0);

/** `url` is public and cacheable forever (a data URL in mock mode). */
export const ImageRef = z.object({ id: z.string(), url: z.string(), width: z.number().int().min(1), height: z.number().int().min(1) });
export type ImageRef = z.infer<typeof ImageRef>;
export const Media = ImageRef.extend({ mime: z.string(), size: count });
export type Media = z.infer<typeof Media>;

// ---------------------------------------------------------------- actions

export const ACTIONS = ["none", "link", "announcement", "news_optin", "url"] as const;
export type MessageAction = (typeof ACTIONS)[number];
export const APP_LINKS = ["home", "today", "arena", "teams", "friends", "inbox", "support", "pro"] as const;
export type AppLink = (typeof APP_LINKS)[number];

export const ACTION_LABEL: Record<MessageAction, string> = {
  none: "No button",
  link: "Open a place in the app",
  announcement: "Open an announcement",
  news_optin: "Turn on News & events",
  url: "Open a web page",
};
export const APP_LINK_LABEL: Record<AppLink, string> = {
  home: "Home",
  today: "Today's puzzle",
  arena: "Arena",
  teams: "Teams",
  friends: "Friends",
  inbox: "Inbox",
  support: "Help & support",
  pro: "Pro (the paywall)",
};
/** Places added in app 1.1.0: older versions land on Home. */
export const NEW_IN_110: ReadonlySet<AppLink> = new Set(["inbox", "support", "pro"]);

// ---------------------------------------------------------------- translation

/**
 * Per locale, any text fields: since the Learn screens (contracts/learn.md) /translate takes and returns loose
 * field maps, and a language may come back without a field the model left out.
 */
export const TranslatedContent = z.partialRecord(z.enum(LOCALES), z.record(z.string(), z.string()));
export type TranslatedContent = z.infer<typeof TranslatedContent>;

export const TranslateResult = z.object({
  content: TranslatedContent,
  model: z.string(),
  usage: z.object({ inputTokens: count, outputTokens: count, costUsd: z.number().min(0) }).nullable(),
});
export type TranslateResult = z.infer<typeof TranslateResult>;

export const messagingApi = {
  /** Fills every target locale from `content.en` (default: all 12). 503 ai_unavailable without an API key. */
  /** Any text fields (`steps.0`, `steps.1`… for arrays); `limits` per field (the API knows the usual ones). */
  translate: (content: TranslatedContent, targets?: Locale[], limits?: Record<string, number>) =>
    request(TranslateResult, "POST", "/translate", { body: { content, ...(targets ? { targets } : {}), ...(limits ? { limits } : {}) } }),
  /** PNG, JPEG or WebP, base64, at most 1.4 MB decoded. The same bytes return the stored image. */
  upload: (mime: MediaMime, data: string) => request(Media, "POST", "/media", { body: { mime, data } }),
};

export function useTranslate() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ content, targets, limits }: { content: TranslatedContent; targets?: Locale[]; limits?: Record<string, number> }) => messagingApi.translate(content, targets, limits),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["audit"] }),
  });
}

export function useUploadMedia() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ mime, data }: { mime: MediaMime; data: string }) => messagingApi.upload(mime, data),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["audit"] }),
  });
}
